import * as React from 'react'
import { Badge, type BadgeProps } from './Badge'

/**
 * AccountState — the IntelliAuth user lifecycle closed set, mirroring the
 * v2 framework's `dp/internal/core/user.AccountState` enum and the wire
 * `components.schemas.User.state` field emitted by the dp-auth-api.
 */
export type AccountState =
  | 'pending'
  | 'active'
  | 'locked'
  | 'blocked'
  | 'deactivated'
  | 'deleted'
  | 'purged'

/**
 * Maps a v2 AccountState onto the Badge variant + display label. Tuned for
 * an admin audience: the badge tone signals operational impact (success
 * for ready-to-go, warning for transient blockers, error for terminal
 * states) rather than just colour-coding the literal enum value.
 */
const USER_STATE_META: Record<AccountState, { variant: BadgeProps['variant']; label: string }> = {
  pending: { variant: 'warning', label: 'Pending' },
  active: { variant: 'success', label: 'Active' },
  locked: { variant: 'warning', label: 'Locked' },
  blocked: { variant: 'error', label: 'Blocked' },
  deactivated: { variant: 'neutral', label: 'Deactivated' },
  deleted: { variant: 'error', label: 'Deleted' },
  purged: { variant: 'outlined', label: 'Purged' },
}

export interface UserStateBadgeProps extends Omit<BadgeProps, 'variant' | 'children'> {
  /** The v2 AccountState; renders as a coloured Badge with the canonical label. */
  state: AccountState
  /** Optional override for the rendered label (defaults to the canonical label per state). */
  label?: string
}

/**
 * UserStateBadge — semantic wrapper over Badge that internalises the
 * AccountState -> Badge variant + label mapping. Use it wherever the
 * admin UI surfaces a user's current state so the cell tone + copy
 * stay consistent across pages.
 */
export const UserStateBadge = React.forwardRef<HTMLSpanElement, UserStateBadgeProps>(
  ({ state, label, size, ...rest }, ref) => {
    const meta = USER_STATE_META[state]
    return (
      <Badge ref={ref} variant={meta.variant} size={size} {...rest}>
        {label ?? meta.label}
      </Badge>
    )
  },
)
UserStateBadge.displayName = 'UserStateBadge'
